import dynamic from 'next/dynamic'

import { ActivityActionButtons } from '~/bootcamp/Activities/types/activities.types'
import { ActivityQuestionType } from '~/types/bootcamp'
import { useAppSelector } from '~/redux/hooks'
import useGetCurrentDifficultyLevel from '~/bootcamp/hooks/useGetCurrentDifficultyLevel'
import MultipleChoice from '~/bootcamp/Activities/SanityActivities/MultipleChoice/MultipleChoice'
import FillInTheBlank from '~/bootcamp/Activities/SanityActivities/FillInTheBlank/FillInTheBlank'

import { selectBootcampActivityEvents, selectCurrentActivityType } from '~/bootcamp/slices/bootcamp-activity.slice'
import { selectBootcampPageActivities } from '~/bootcamp/slices/bootcamp-page.slice'
import { getActivityProperty } from '~/bootcamp/pages/BootcampView/BootcampView.utils'

import { ActivityKey } from '~/bootcamp/types/bootcamp.types'

const Categorization = dynamic(() => import('~/bootcamp/Activities/SanityActivities/Categorization/Categorization'))
const MatchingPairs = dynamic(() => import('~/bootcamp/Activities/SanityActivities/MatchingPairs/MatchingPairs'))
const OrderElements = dynamic(() => import('~/bootcamp/Activities/SanityActivities/OrderElements/OrderElements'))
const NumberLine = dynamic(() => import('~/bootcamp/Activities/SanityActivities/NumberLine/NumberLine'))
const MeasureTheItem = dynamic(() => import('~/bootcamp/Activities/SanityActivities/MeasureTheItem/MeasureTheItem'))
const CircleCombinations = dynamic(
  () => import('~/bootcamp/Activities/SanityActivities/CircleCombinations/CircleCombinations'),
)

interface SanityActivityRendererProps {
  answerState: boolean | null
  levelId: string
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null, questionId?: string) => void
  actionButtonsFC?: React.FC<ActivityActionButtons>
}

function SanityActivityRenderer({
  actionButtonsFC,
  answerState,
  levelId,
  onAnswerStateChange,
  onNextQuestionClick,
}: SanityActivityRendererProps): React.ReactElement {
  const events = useAppSelector(selectBootcampActivityEvents)
  const activities = useAppSelector(selectBootcampPageActivities) || []
  const activityType = useAppSelector(selectCurrentActivityType)
  const [difficultyLevel] = useGetCurrentDifficultyLevel(events, activities)

  const questionType = getActivityProperty(activities, 'questionType', activityType, difficultyLevel)

  switch (questionType) {
    case ActivityQuestionType.multipleChoiceQuestion: {
      const multipleChoiceQuestions = getActivityProperty(
        activities,
        ActivityKey.multipleChoiceActivity,
        activityType,
        difficultyLevel,
      )

      if (!multipleChoiceQuestions || !multipleChoiceQuestions.length) return <></>

      return (
        <MultipleChoice
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          autofocus
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={multipleChoiceQuestions}
        />
      )
    }

    case ActivityQuestionType.fillInTheBlanks: {
      const fillInTheBlankQuestions = getActivityProperty(
        activities,
        ActivityKey.fillInTheBlankActivity,
        activityType,
        difficultyLevel,
      )

      if (!fillInTheBlankQuestions || !fillInTheBlankQuestions.length) return <></>

      const keyboardLayoutId = getActivityProperty(activities, 'keyboardLayout', activityType, difficultyLevel)

      return (
        <FillInTheBlank
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          autofocus
          difficultyLevel={difficultyLevel}
          keyboardLayoutId={keyboardLayoutId}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={fillInTheBlankQuestions}
        />
      )
    }

    case ActivityQuestionType.circleCombinations: {
      const circleCombinationsQuestions = getActivityProperty(
        activities,
        ActivityKey.circleCombinationsActivity,
        activityType,
        difficultyLevel,
      )

      if (!circleCombinationsQuestions || !circleCombinationsQuestions.length) return <></>

      return (
        <CircleCombinations
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          autofocus
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={circleCombinationsQuestions}
        />
      )
    }

    case ActivityQuestionType.categorization: {
      const categorizationQuestions = getActivityProperty(
        activities,
        ActivityKey.categorizationActivity,
        activityType,
        difficultyLevel,
      )
      if (!categorizationQuestions || !categorizationQuestions.length) return <></>

      return (
        <Categorization
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={categorizationQuestions}
        />
      )
    }
    case ActivityQuestionType.orderElements: {
      const orderElementsQuestions = getActivityProperty(
        activities,
        ActivityKey.orderElementsActivity,
        activityType,
        difficultyLevel,
      )

      if (!orderElementsQuestions || !orderElementsQuestions.length) return <></>

      return (
        <OrderElements
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={orderElementsQuestions}
        />
      )
    }

    case ActivityQuestionType.matchingPairs: {
      const matchingPairsQuestions = getActivityProperty(
        activities,
        ActivityKey.matchingPairsActivity,
        activityType,
        difficultyLevel,
      )

      if (!matchingPairsQuestions || !matchingPairsQuestions.length) return <></>

      return (
        <MatchingPairs
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          autofocus
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={matchingPairsQuestions}
        />
      )
    }

    case ActivityQuestionType.numberLine: {
      const numberLineQuestions = getActivityProperty(
        activities,
        ActivityKey.numberLineActivity,
        activityType,
        difficultyLevel,
      )

      if (!numberLineQuestions || !numberLineQuestions.length) return <></>

      return (
        <NumberLine
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          autofocus
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={numberLineQuestions}
        />
      )
    }

    case ActivityQuestionType.measureTheItem: {
      const measureTheItemQuestions = getActivityProperty(
        activities,
        ActivityKey.measureTheItemActivity,
        activityType,
        difficultyLevel,
      )

      if (!measureTheItemQuestions || !measureTheItemQuestions.length) return <></>

      return (
        <MeasureTheItem
          actionButtonsFC={actionButtonsFC}
          activityType={activityType}
          answerState={answerState}
          autofocus
          difficultyLevel={difficultyLevel}
          levelId={levelId}
          onAnswerStateChange={onAnswerStateChange}
          onNextQuestionClick={onNextQuestionClick}
          questions={measureTheItemQuestions}
        />
      )
    }

    default:
      return <></>
  }
}

export default SanityActivityRenderer
